import { useCallback, useEffect, useRef, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";

/**
 * Realtime channel health: notice a dead channel, recover from it, and — critically —
 * STOP trying when recovery clearly isn't working.
 *
 * WHY THIS EXISTS: most channels called a bare `.subscribe()` with no callback, so when
 * a socket died (laptop sleep, wifi switch, a backend deploy) nothing noticed. The
 * features that hurt most have no polling fallback — DTMF/Key Grabber, the incoming-call
 * widget, dialer answered-detection — so an agent would sit there while calls silently
 * stopped surfacing.
 *
 * ⚠️ WHY THE BACKOFF IS NOT OPTIONAL — this bit me. The first version simply bumped a
 * token on failure, and that token was an effect dependency:
 *
 *     subscribe() → CHANNEL_ERROR → setToken(t+1) → effect reruns → subscribe() → ...
 *
 * With no delay and no cap, a channel that fails consistently spins as fast as React can
 * re-render, saturates the main thread and hangs the tab (Chrome: RESULT_CODE_HUNG).
 * A self-healing mechanism that can melt the browser is worse than no mechanism at all.
 *
 * So: exponential backoff, a hard attempt cap, and the counter resets only on a real
 * SUBSCRIBED. After the cap we stop and surface `gaveUp` — degraded but responsive,
 * which is the right failure mode.
 *
 * ⚠️ WHY "RESET ON SUBSCRIBED" IS NOT ENOUGH — 2026-08-18, reported as "slow to load the
 * next caller on powerdial". The console showed this, once a second, forever:
 *
 *     [realtime:incoming-call] CLOSED; retry 1/5 in 1000ms     16:33:29.747
 *     [realtime:incoming-call] CLOSED; retry 1/5 in 1000ms     16:33:30.755
 *     [realtime:incoming-call] CLOSED; retry 1/5 in 1000ms     16:33:31.765   ...
 *
 * Always `1/5`, always 1000ms apart — the cap NEVER engaged. Two facts combine into a
 * perpetual-motion machine:
 *
 *   1. `supabase.removeChannel()` fires the `.subscribe()` callback with CLOSED
 *      (realtime-js binds `_onClose(() => callback('CLOSED'))`). Our own React cleanup
 *      therefore reports a failure — one we caused.
 *   2. The rejoin runs as `[deps..., rejoinToken]`, so bumping the token re-runs the
 *      effect, whose cleanup removes the channel, which reports CLOSED, which bumps
 *      the token again. It re-subscribes fine in between, and that SUBSCRIBED refunded
 *      the whole attempt budget — so every cycle logged a fresh `1/5`.
 *
 * One transient join failure was enough to seed it, and it then ran until the tab was
 * reloaded: ~1 socket join+leave per second, plus a window each cycle where broadcasts
 * are simply DROPPED. On the dialer that window is the answered-detection event, which
 * is why the visible symptom was a slow next caller rather than anything about sockets.
 *
 * THE FIX: a SUBSCRIBED only refunds the budget once it has HELD for `MIN_STABLE_MS`.
 * A join that dies immediately no longer buys another attempt, so a flapping channel
 * escalates 1→5 and stops, exactly as the cap always intended.
 */

export type RealtimeDeadReason = "CHANNEL_ERROR" | "TIMED_OUT" | "CLOSED";

const MAX_ATTEMPTS = 5;
/**
 * How long a subscription must survive before it counts as real and refunds the attempt
 * budget. Comfortably longer than the 1s first backoff, so a join-then-immediately-close
 * flap can never top the budget back up. Short enough that a genuinely healthy channel
 * has its budget back long before the next unrelated blip.
 */
const MIN_STABLE_MS = 5_000;
/** 1s, 2s, 4s, 8s, 16s — capped so a long outage doesn't schedule absurd timers. */
const backoffMs = (attempt: number) => Math.min(1_000 * 2 ** attempt, 16_000);

export interface RealtimeRejoin {
  /** Include in the subscribing effect's dependency array. */
  rejoinToken: number;
  /** Pass straight to `.subscribe(...)`. */
  handleStatus: (status: string, err?: Error) => void;
  /** True once we've stopped retrying — surface this rather than pretending it's live. */
  gaveUp: boolean;
  /** Manual retry, e.g. from a "Reconnect" button. Clears the attempt counter. */
  retryNow: () => void;
}

export function useRealtimeRejoin(label: string): RealtimeRejoin {
  const [rejoinToken, setRejoinToken] = useState(0);
  const [gaveUp, setGaveUp] = useState(false);
  const attempts = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Pending "this join has held long enough" timer — see MIN_STABLE_MS. */
  const stable = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (stable.current) clearTimeout(stable.current);
    },
    [],
  );

  const handleStatus = useCallback(
    (status: string, err?: Error) => {
      if (status === "SUBSCRIBED") {
        // A join only refunds the budget once it HOLDS. Refunding the instant we connect
        // is what let a join/close flap log `retry 1/5` forever — see the header note.
        if (stable.current) clearTimeout(stable.current);
        stable.current = setTimeout(() => {
          attempts.current = 0;
        }, MIN_STABLE_MS);
        if (gaveUp) setGaveUp(false);
        return;
      }

      if (status !== "CHANNEL_ERROR" && status !== "TIMED_OUT" && status !== "CLOSED") {
        return;
      }

      // This join did not survive MIN_STABLE_MS, so it does not get to refund anything.
      if (stable.current) {
        clearTimeout(stable.current);
        stable.current = null;
      }

      if (attempts.current >= MAX_ATTEMPTS) {
        if (!gaveUp) {
          console.warn(
            `[realtime:${label}] gave up after ${MAX_ATTEMPTS} attempts — running degraded`,
          );
          setGaveUp(true);
        }
        return;
      }

      const wait = backoffMs(attempts.current);
      attempts.current += 1;
      console.warn(
        `[realtime:${label}] ${status}${err?.message ? ` — ${err.message}` : ""}; ` +
          `retry ${attempts.current}/${MAX_ATTEMPTS} in ${wait}ms`,
      );

      if (timer.current) clearTimeout(timer.current);
      // Deferred, never synchronous: bumping the token inside the status callback is
      // what turned this into a render loop in the first place.
      timer.current = setTimeout(() => setRejoinToken((t) => t + 1), wait);
    },
    [label, gaveUp],
  );

  const retryNow = useCallback(() => {
    if (stable.current) {
      clearTimeout(stable.current);
      stable.current = null;
    }
    attempts.current = 0;
    setGaveUp(false);
    setRejoinToken((t) => t + 1);
  }, []);

  return { rejoinToken, handleStatus, gaveUp, retryNow };
}

/** True once the channel is joined — handy for gating `track()` / presence writes. */
export function isChannelJoined(channel: RealtimeChannel | null | undefined): boolean {
  return channel?.state === "joined";
}
